import { handleRegularPayment } from './handleRegularPayment';
import { toastifyMessage } from '../handlers/toastifyMessage';

// Samsung Pay Web Checkout SDK (script tag added in public/index.html).
// Docs: https://developer.samsung.com/pay/merchant/web-checkout-sdk/overview.html
// This replaces the old redirect-based integration (window.SamsungPay.connect +
// backend /validate_samsung + /get_samsung_token), which called an API the
// current Samsung SDK no longer exposes. The new SDK resolves a payment
// credential in-page via a promise - no redirect, no ref_id, no resume effect.

const SAMSUNG_PAY_ENVIRONMENT =
  process.env.REACT_APP_SAMSUNG_PAY_ENVIRONMENT || 'PRODUCTION';

const buildPaymentMethods = () => ({
  version: '2',
  serviceId: process.env.REACT_APP_SAMSUNG_SERVICE_ID,
  protocol: 'PROTOCOL_3DS',
  allowedBrands: ['visa', 'mastercard']
});

const buildTransactionDetail = (amount) => ({
  orderNumber: `ERC${Date.now()}`,
  merchant: {
    name: 'Emirates Red Crescent',
    url: 'www.emiratesrc.ae',
    countryCode: 'AE'
  },
  amount: {
    option: 'FORMAT_TOTAL_PRICE_ONLY',
    currency: 'AED',
    total: String(amount)
  }
});

const isUserCancel = (error) => {
  const text = [error?.code, error?.name, error?.message, error?.statusCode, String(error)]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
  return text.includes('CANCEL');
};

// A second invoke while the sheet is open corrupts the SDK's DOM (see the
// paymentInFlight note in CheckOutPage); direct-pay pages have no guard of
// their own now that the overlay no longer blocks clicks behind the sheet.
let samsungPayInFlight = false;

const runSamsungPay = async (...args) => {
  if (samsungPayInFlight) return;
  samsungPayInFlight = true;
  try {
    await runSamsungPaySheet(...args);
  } finally {
    samsungPayInFlight = false;
  }
};

// Runs the Samsung Pay sheet for `amount`, and on success forwards the raw
// encrypted payment credential (paymentCredential['3DS'].data) to the existing
// /init_pay_web flow via handleRegularPayment with payment_type "SAMSUNG" -
// exactly the token shape the backend's SAMSUNG branch / captureFromSamsungPay
// already expect, so no backend change is needed.
const runSamsungPaySheet = async (amount, bag, dispatch, history, recieptFor, onSheetOpening) => {
  if (!window.SamsungPay) {
    toastifyMessage("You Don't Have Samsung Pay", undefined, 'warning');
    return;
  }

  const client = new window.SamsungPay.PaymentClient({
    environment: SAMSUNG_PAY_ENVIRONMENT
  });
  const paymentMethods = buildPaymentMethods();

  const ready = await client
    .isReadyToPay(paymentMethods)
    .catch(() => ({ result: false }));
  if (!ready?.result) {
    toastifyMessage("You Don't Have Samsung Pay", undefined, 'warning');
    return;
  }

  // Samsung's own Web Checkout SDK takes over the UI from here (its QR sheet /
  // countdown / in-progress spinner) and can stay open for several minutes
  // while the donor completes the payment on their phone. Let the caller drop
  // its own site-wide loading overlay now instead of leaving it stacked behind
  // Samsung's sheet for the whole wait — that's what made it look stuck.
  onSheetOpening?.();

  let paymentCredential;
  try {
    paymentCredential = await client.loadPaymentSheet(
      paymentMethods,
      buildTransactionDetail(amount)
    );
  } catch (error) {
    // No payment was attempted, so there's nothing to notify(). A cancel stays
    // silent, but any other failure (e.g. service id / domain not registered)
    // must be surfaced or the button looks dead.
    console.error('Samsung Pay error:', error);
    if (!isUserCancel(error)) {
      toastifyMessage(
        'Samsung Pay could not complete the payment. Please try again or use another payment method.',
        undefined,
        'error'
      );
    }
    return;
  }

  const token = paymentCredential?.['3DS']?.data;
  if (!token) {
    toastifyMessage('Something went wrong...!', undefined, 'error');
    client.notify({ status: 'ERRED', provider: 'MPGS' });
    return;
  }

  try {
    const res = await handleRegularPayment(
      { tokn: token, payment_type: 'SAMSUNG', bag },
      dispatch,
      null,
      recieptFor
    );
    if (res && res.status) {
      client.notify({ status: 'CHARGED', provider: 'MPGS' });
      history.push(`/receipt/${res.mco}`);
    } else {
      client.notify({ status: 'REJECTED', provider: 'MPGS' });
    }
  } catch (error) {
    client.notify({ status: 'ERRED', provider: 'MPGS' });
    toastifyMessage('Something went wrong...!', undefined, 'error');
  }
};

// Cart / checkout-page flow. body: { amount, cart }
export const handleSamsungPayment = async (
  body,
  dispatch,
  history,
  recieptFor,
  onSheetOpening
) => {
  if (body.amount < 1) {
    toastifyMessage('Please select an amount to donate', undefined, 'error');
    return;
  }
  await runSamsungPay(
    body.amount,
    body.cart,
    dispatch,
    history,
    recieptFor,
    onSheetOpening
  );
};

// Single-item "direct pay" flow (sponsor/human-case/sukuk/project-share
// pages). body: { amount, item }
export const handleSamsungDirectPayment = async (
  body,
  dispatch,
  history,
  recieptFor,
  onSheetOpening
) => {
  if (body.item.length > 1) {
    let itemsAboveFive = body.item.filter((item) => item.amount >= 5);
    if (itemsAboveFive.length < body.item.length) {
      toastifyMessage('Can not pay less than 5 AED', undefined, 'warning');
      return;
    }
  }
  if (body.amount < 5) {
    toastifyMessage('Can not pay less than 5 AED', undefined, 'warning');
    return;
  }
  await runSamsungPay(
    body.amount,
    body.item,
    dispatch,
    history,
    recieptFor,
    onSheetOpening
  );
};
